import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Mail, Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import posthog from "posthog-js";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SubmitCafeDialog } from "@/components/SubmitCafeDialog";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/hooks/useAuth";
import { isNativeShell } from "@/lib/oauthRedirect";
import { bindPushIdentity, readNativePushEnabled, requestPushPermission, waitForPushGrant } from "@/lib/pushPermission";
// Small copy for the 96 px card spot; the full art stays for the badge itself.
import foundingBadgeArt from "@/assets/badges/founding-member-card.webp";
import { resolvePlaceName, roundCoord, type ResolvedPlace } from "@/lib/resolvePlaceName";
import { insertCityWaitlistRow, type CityWaitlistSource } from "@/lib/cityWaitlistVote";

export type { CityWaitlistSource };

interface CityWaitlistCardProps {
  city: string;
  lat: number;
  lng: number;
  /** Which surface produced this demand signal. Stored on the row. */
  source: CityWaitlistSource;
}

const emailSchema = z.string().trim().email().max(255);

function safeCapture(event: string, props: Record<string, unknown>) {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics never blocks flow */
  }
}

export function CityWaitlistCard({ city, lat, lng, source }: CityWaitlistCardProps) {
  const { user } = useAuth();
  const isGpsVariant = city === "Current Location";

  // "Current Location" is useless as a demand signal, so resolve the point to
  // a real place before anything is recorded. Fails soft to the raw label.
  const [resolved, setResolved] = useState<ResolvedPlace | null>(null);
  const [placeSettled, setPlaceSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setResolved(null);
    setPlaceSettled(false);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setPlaceSettled(true);
      return;
    }
    resolvePlaceName(lat, lng)
      .then((place) => {
        if (cancelled) return;
        setResolved(place);
        setPlaceSettled(true);
      })
      .catch(() => {
        if (!cancelled) setPlaceSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  const displayCity = resolved?.label ?? city;
  const isUnresolvedGps = isGpsVariant && !resolved;
  const cityToken = isUnresolvedGps ? "your city" : displayCity;
  const title = `Bring Grounded to ${cityToken}`;

  const locationProps = {
    searched_city: displayCity,
    city: resolved?.city ?? null,
    state: resolved?.state ?? null,
    lat: roundCoord(lat),
    lng: roundCoord(lng),
    source,
  };
  const goldLine = `Early voters earn this Founding Member badge when ${cityToken} launches.`;

  const [voted, setVoted] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [pushState, setPushState] = useState<"unknown" | "ask" | "on" | "hidden">("unknown");

  // Signed in, inside the iPhone app, pushes not on yet: offer them after voting.
  useEffect(() => {
    if (!confirmed || !user || pushState !== "unknown") return;
    if (!isNativeShell()) { setPushState("hidden"); return; }
    let cancelled = false;
    readNativePushEnabled()
      .then((on) => { if (!cancelled) setPushState(on ? "hidden" : "ask"); })
      .catch(() => { if (!cancelled) setPushState("hidden"); });
    return () => { cancelled = true; };
  }, [confirmed, user, pushState]);

  const handleTurnOnPush = async () => {
    try {
      const outcome = await requestPushPermission();
      safeCapture("city_waitlist_push_prompt", { ...locationProps, outcome });
      if (outcome === "granted") { setPushState("on"); return; }
      if (outcome !== "requested") { setPushState("hidden"); return; }
      // The iPhone popup is up. Say "on" only once permission is really granted.
      // A later Allow is still linked to the account on the next app open.
      const granted = await waitForPushGrant();
      setPushState(granted ? "on" : "hidden");
      if (granted && user) bindPushIdentity(user.id);
    } catch {
      setPushState("hidden");
    }
  };

  const shownTracked = useRef<string | null>(null);
  useEffect(() => {
    // Hold the event until the place name is known, so the property is useful.
    if (!placeSettled) return;
    if (shownTracked.current === displayCity) return;
    shownTracked.current = displayCity;
    safeCapture("city_waitlist_shown", {
      ...locationProps,
      is_signed_in: !!user,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCity, placeSettled, user]);

  /**
   * Single insert path. city_waitlist is insert only by design, so the email
   * follow up writes a NEW row rather than updating the vote row.
   */
  const insertRow = (payload: { email: string | null }) =>
    insertCityWaitlistRow({
      place: {
        searched_city: displayCity,
        city: resolved?.city ?? null,
        state: resolved?.state ?? null,
        lat,
        lng,
      },
      email: payload.email,
      userId: user?.id ?? null,
      source,
    });

  const handleVote = async (guestEmail: string | null = null) => {
    // Writing before the place resolves would store "Current Location" as the
    // demand signal, which is exactly what this card exists to avoid.
    if (!placeSettled) return;
    setIsSubmitting(true);
    try {
      // Signed in users already gave us their email; guests may give one with
      // the vote ("Vote and notify me") so it is one row.
      const votingEmail = user?.email ? user.email.toLowerCase() : guestEmail;
      const error = await insertRow({ email: votingEmail });

      if (error && error.code !== "23505") throw error;

      setVoted(true);
      if (votingEmail) { setConfirmed(true); setConfirmedEmail(votingEmail); }
      toast.success("Your vote is counted!");
      safeCapture("city_waitlist_voted", {
        ...locationProps,
        is_signed_in: !!user,
        email_provided: !!votingEmail,
      });
      if (votingEmail) {
        safeCapture("city_waitlist_signed_up", {
          ...locationProps,
          is_signed_in: !!user,
          already_subscribed: error?.code === "23505",
        });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Same reason as handleVote: never store an unresolved place.
    if (!placeSettled) return;
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast.error("Please enter a valid email.");
      return;
    }
    const normalized = parsed.data.toLowerCase();

    setIsSubmitting(true);
    try {
      const error = await insertRow({ email: normalized });

      if (error) {
        if (error.code === "23505") {
          toast.info("You're already on the list!");
          setConfirmed(true);
          setConfirmedEmail(normalized);
          safeCapture("city_waitlist_signed_up", {
            ...locationProps,
            is_signed_in: !!user,
            already_subscribed: true,
          });
          return;
        }
        throw error;
      }

      toast.success("You're on the list!");
      setConfirmed(true);
      setConfirmedEmail(normalized);
      safeCapture("city_waitlist_signed_up", {
        ...locationProps,
        is_signed_in: !!user,
        already_subscribed: false,
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoteAndNotify = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast.error("Please enter a valid email.");
      return;
    }
    void handleVote(parsed.data.toLowerCase());
  };

  const privacyLine = (
    <p className="text-xs text-muted-foreground text-left">
      We'll only email you about Grounded in your city.{" "}
      <Link to="/privacy" className="underline hover:text-foreground">
        Privacy Policy
      </Link>
    </p>
  );

  const emailField = (id: string) => (
    <div className="space-y-1.5 text-left">
      <Label htmlFor={id}>Your email</Label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          id={id}
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-9"
          required
        />
      </div>
    </div>
  );

  const goldBox = (text: string) => (
    <p className="rounded-lg border border-caramel/30 bg-caramel/10 px-3 py-2 text-sm text-foreground">
      {text}
    </p>
  );

  return (
    <div className="text-center py-12 px-6 card-craft rounded-xl shadow-lg max-w-lg mx-auto">
      <img
        src={foundingBadgeArt}
        alt="Founding Member badge"
        width={96}
        height={96}
        loading="lazy"
        className="mx-auto mb-4 h-24 w-24"
      />

      {!voted && (
        <>
          <h3 className="font-display text-xl font-semibold text-foreground mb-3">{title}</h3>
          {user ? (
            <div className="space-y-4">
              <p className="text-muted-foreground leading-relaxed">
                Your vote helps decide which city we score next.
              </p>
              {goldBox(goldLine)}
              <Button onClick={() => handleVote()} disabled={isSubmitting || !placeSettled} className="w-full sm:w-auto px-8">
                {isSubmitting ? "…" : `Add my vote for ${cityToken}`}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleVoteAndNotify} className="space-y-4 max-w-sm mx-auto">
              <p className="text-muted-foreground leading-relaxed">
                Your vote helps decide which city we score next. Leave your email and we'll let you know when {cityToken}'s work cafés go live.
              </p>
              {goldBox(goldLine)}
              {emailField("city-waitlist-email")}
              <Button type="submit" disabled={isSubmitting || !placeSettled || !email.trim()} className="w-full">
                {isSubmitting ? "…" : "Vote and notify me"}
              </Button>
              {privacyLine}
              <Button type="button" variant="link" onClick={() => handleVote()} disabled={isSubmitting || !placeSettled}>
                Just add my vote
              </Button>
            </form>
          )}
        </>
      )}

      {voted && !confirmed && (
        <form onSubmit={handleGuestSubmit} className="space-y-4 max-w-sm mx-auto">
          <h3 className="font-display text-xl font-semibold text-foreground">{`Your vote for ${cityToken} is counted.`}</h3>
          <p className="text-muted-foreground">Want an email when it launches? It also saves your Founding Member badge.</p>
          {emailField("city-waitlist-followup-email")}
          <Button type="submit" disabled={isSubmitting || !placeSettled || !email.trim()} className="w-full">
            {isSubmitting ? "…" : "Notify me"}
          </Button>
          {privacyLine}
        </form>
      )}

      {voted && confirmed && !user && (
        <div className="space-y-4 max-w-sm mx-auto">
          <h3 className="font-display text-xl font-semibold text-foreground">{`You're on the list for ${cityToken}.`}</h3>
          <p className="text-muted-foreground">We'll email you once its work cafés are on Grounded.</p>
          {goldBox("Your Founding Member badge is saved to this email. Make an account with it to claim the badge at launch.")}
          <Button asChild className="w-full">
            <Link to={`/auth?mode=signup&email=${encodeURIComponent(confirmedEmail ?? "")}`}>Create my account</Link>
          </Button>
        </div>
      )}

      {voted && confirmed && user && (
        <div className="space-y-4 max-w-sm mx-auto">
          <div className="flex items-start gap-2 justify-center text-sage font-medium">
            <Check className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <span className="text-left">{`You're on the list. We'll email you when Grounded reaches ${cityToken}.`}</span>
          </div>
          {pushState === "ask" && (
            <div className="space-y-2">
              <p className="text-sm text-foreground">Want a heads up on your phone too?</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={handleTurnOnPush}>Turn on notifications</Button>
                <Button variant="ghost" onClick={() => setPushState("hidden")}>Not now</Button>
              </div>
            </div>
          )}
          {pushState === "on" && (
            <p className="text-sm text-foreground">{`Notifications are on. We'll send a push when ${cityToken} launches.`}</p>
          )}
          {goldBox(`Your Founding Member badge unlocks the day ${cityToken} goes live.`)}
        </div>
      )}

      <div className="pt-4 mt-6 border-t border-border/30">
        <p className="text-sm text-muted-foreground mb-2">Know a great work cafe there?</p>
        <SubmitCafeDialog
          entryPoint="city_waitlist_card"
          trigger={
            <Button variant="outline" size="sm" className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Submit a Cafe
            </Button>
          }
        />
      </div>
    </div>
  );
}
